<script setup lang="ts">
import type { NotificationExpandedDTO } from '@/api/vtnz-backend/notifications';
import { useSavedLocationStore } from '@/domains/locations/savedLocationStore';
import { resolveNotificationTarget } from '@/domains/notifications/types';
import { useTripStore } from '@/domains/trips/tripStore';
import { useUserStore } from '@/domains/user/userStore';
import { useVehicleStore } from '@/domains/vehicles';
import { useDashboardNavStore } from '@/stores/dashboardNavStore';
import { computed } from 'vue';


const { notification } = defineProps<{ notification: NotificationExpandedDTO }>()

const userStore = useUserStore();
const vehicleStore = useVehicleStore();
const tripStore = useTripStore();
const savedLocationStore = useSavedLocationStore();
const dashboardNavStore = useDashboardNavStore();

const creatingUser = computed(() => userStore.getUsers.find((u) => u.id && u.id === notification.created_by))

const showAvatar = computed(
  () => !!creatingUser.value?.avatarURL
)

const avatarSrc = computed<string | undefined>(() => {
  if (showAvatar.value){
    creatingUser.value?.avatarURL
  }
  return undefined
})

const icon = computed(() => {
  return 'mdi-bell'
  // TODO: expand when backend support becomes available
})

/**
 * Resolved once, up front, so the template can decide whether this
 * notification is clickable at all - an older notification, or one whose
 * `type` doesn't match any of the (placeholder, unconfirmed) values in
 * resolveNotificationTarget, resolves to "none" and stays inert rather than
 * navigating somewhere wrong.
 */
const navigationTarget = computed(() => resolveNotificationTarget(notification));
const isClickable = computed(() => navigationTarget.value.kind !== 'none');

/**
 * Translates the backend/external ids resolveNotificationTarget hands back
 * into whatever local id each destination's dashboardNavStore.showX call
 * actually keys on - Vehicle/Trip/SavedLocation use a local id distinct
 * from their externalId, User doesn't.
 */
function handleClick() {
  const target = navigationTarget.value;

  switch (target.kind) {
    case 'trip': {
      const trip = tripStore.getTripByExternalId(target.tripExternalId);
      if (trip) dashboardNavStore.showTripDetails(trip.id);
      return;
    }
    case 'vehicle': {
      const vehicle = vehicleStore.getVehicleByExternalId(target.vehicleExternalId);
      if (vehicle) dashboardNavStore.showVehicleDetails(vehicle.id);
      return;
    }
    case 'vehicle-group': {
      dashboardNavStore.showVehicleList(undefined, target.vehicleExternalIds);
      return;
    }
    case 'location': {
      const location = savedLocationStore.getLocationByExternalId(target.locationId);
      if (location) dashboardNavStore.showLocationDetails(location.localId);
      return;
    }
    case 'member':
      dashboardNavStore.showMemberDetails(target.userId);
      return;
    case 'none':
      return;
  }
}
</script>
<template>
  <v-list-item
    class="mb-3 rounded-lg bg-pwt_off_white pa-5"
    :class="{ 'cursor-pointer': isClickable }"
    @click="isClickable && handleClick()"
  >
    <!-- prepend: avatar -->
    <template v-if="showAvatar" v-slot:prepend>
      <v-avatar class="mr-3 mt-1">
        <img v-if="avatarSrc" :src="avatarSrc"  width="100%" height="auto"/>
        <v-icon v-else icon="custom:avatar" size="100%" />
      </v-avatar>
    </template>

    <!-- prepend: icon -->
    <template v-else v-slot:prepend>
      <v-icon :icon="icon" color="primary" size="36" />
    </template>

    <!-- content -->
    <v-list-item-title>{{ notification.message}}</v-list-item-title>

    <!-- append -->
  </v-list-item>
</template>
